import { isEmpty } from "lodash";
import React, { lazy, Suspense, useReducer } from "react";
import { navigate } from "gatsby";
import queryString from "query-string";

import { getFormattedAttributes } from "~/utilities/format";

import Skeleton from "~/components/loaders/Skeleton";

import useRefDesignTableKeys from "~/hooks/useRefDesignTableKeys";
import { useLocationContext } from "~/context/LocationContext";

const ProductTableSection = lazy(
    () => import("~/components/ProductTableSection"),
);
const RefDesignForProductCategory = lazy(
    () => import("~/components/RefDesignTable/RefDesignForProductCategory"),
);
const TableKey = lazy(() => import("~/components/RefDesignTable/TableKey"));
const ProductTableTabs = lazy(() => import("~/components/ProductTableTabs"));

const reducer = (state, action) => {
    const { showTableKeys, currentTab, tabChanged } = action;
    switch (action.type) {
        case "HANDLE_TAB_CHANGE":
            return {
                ...state,
                showTableKeys,
                currentTab,
                tabChanged,
            };
        default:
            return state;
    }
};

const initialState = {
    showTableKeys: false,
    currentTab: "",
    tabChanged: false,
    defaultIndex: 0,
};

const setInitialState = (location) => {
    const initialQueryParams = queryString.parse(location.search);
    let currentTab = "";
    let defaultIndex = 0;
    let showTableKeys = false;

    if (initialQueryParams?.tab) {
        const { tab } = initialQueryParams;
        currentTab = tab;

        if (tab === "by-application") {
            defaultIndex = 1;
            showTableKeys = true;
        }
    }

    return {
        ...initialState,
        currentTab,
        defaultIndex,
        showTableKeys,
    };
};

const getTableFilters = (categoryData, parentCategory) => {
    let tableFilters = parentCategory?.categoryData?.tableFilters ?? [];

    if (!isEmpty(categoryData?.tableFilters)) {
        tableFilters = categoryData.tableFilters;
    }

    return tableFilters;
};

const getTableHeaders = (categoryData, parentCategory) => {
    let tableHeaders = parentCategory?.categoryData?.tableHeaders ?? [];

    if (!isEmpty(categoryData?.tableHeaders)) {
        tableHeaders = categoryData.tableHeaders;
    }

    return tableHeaders;
};

const hideTable = (products, headers) => {
    return isEmpty(products?.nodes) || isEmpty(headers);
};

const getProductTableTabs = (
    productTableProps,
    referenceDesignsCategory,
    currentTab,
    tabChanged,
) => {
    const productTableTab = {
        title: referenceDesignsCategory.productTabLabel,
        content: (
            <ProductTableSection
                {...productTableProps}
                includeTitle={false}
                {...{ currentTab, tabChanged }}
            />
        ),
    };

    const referenceDesignTab = {
        title: referenceDesignsCategory.applicationTabLabel,
        content: (
            <RefDesignForProductCategory
                allProducts={productTableProps.products}
                refDesignProps={referenceDesignsCategory.referenceDesignsTable}
                {...{ currentTab, tabChanged }}
            />
        ),
    };

    return [productTableTab, referenceDesignTab];
};

const ProductCategoryTableSection = ({ data }) => {
    const {
        products,
        parentCategory,
        wpProductCategory: {
            title,
            slug,
            categoryData,
            referenceDesignsCategory,
        },
    } = data;
    const { refDesignKeys } = useRefDesignTableKeys();
    const { location } = useLocationContext();
    const useTabs = referenceDesignsCategory?.showByApplicationTableView;
    const [state, dispatch] = useReducer(reducer, initialState, () => {
        return setInitialState(location);
    });
    const { currentTab, tabChanged, showTableKeys, defaultIndex } = state;

    const formattedProductTableHeaders = getFormattedAttributes(
        null,
        getTableHeaders(categoryData, parentCategory),
    );

    const formattedProductTableFilters = getFormattedAttributes(
        null,
        getTableFilters(categoryData, parentCategory),
    );

    if (hideTable(products, formattedProductTableHeaders)) {
        return null;
    }

    const {
        productTableSectionId,
        initialTableSortColumn,
        initialTableSortDirection,
        secondaryTableSortColumn,
        secondaryTableSortDirection,
        tertiaryTableSortColumn,
        tertiaryTableSortDirection,
        firstColumnStaticOnMobile,
        addPaginationToProductTable,
        noCategoryRedirectForGroupedProduct,
    } = categoryData;

    const productTableProps = {
        linkProducts: true,
        sectionId: useTabs ? "" : productTableSectionId,
        ariaLabel: title,
        productFilters: formattedProductTableFilters,
        productHeaders: formattedProductTableHeaders,
        initialColumnSort: initialTableSortColumn,
        initialColumnSortDirection: initialTableSortDirection,
        secondaryColumnSort: secondaryTableSortColumn,
        secondaryColumnSortDirection: secondaryTableSortDirection,
        tertiaryColumnSort: tertiaryTableSortColumn,
        tertiaryColumnSortDirection: tertiaryTableSortDirection,
        isCategoryPage: true,
        isProductPage: true,
        firstColumnStaticOnMobile,
        products: products.nodes,
        withPagination: addPaginationToProductTable,
        subcategory: noCategoryRedirectForGroupedProduct
            ? null
            : {
                  slug,
                  name: title,
              },
    };

    const handleOnSelect = (index) => {
        const isAppTab = index === 1;
        const tabName = isAppTab ? "by-application" : "product-name";

        dispatch({
            type: "HANDLE_TAB_CHANGE",
            showTableKeys: isAppTab,
            currentTab: tabName,
            tabChanged: true,
        });

        navigate(`?tab=${tabName}#${productTableSectionId}`);
    };

    return (
        <Suspense fallback={<Skeleton variant="block" />}>
            {useTabs ? (
                <ProductTableTabs
                    title="Products"
                    sectionId={productTableSectionId}
                    tabs={getProductTableTabs(
                        productTableProps,
                        referenceDesignsCategory,
                        currentTab,
                        tabChanged,
                    )}
                    tabsLabel={referenceDesignsCategory.toggleLabel}
                    {...{ handleOnSelect, defaultIndex }}
                >
                    {showTableKeys && <TableKey {...{ refDesignKeys }} />}
                </ProductTableTabs>
            ) : (
                <ProductTableSection {...productTableProps} />
            )}
        </Suspense>
    );
};

export default ProductCategoryTableSection;
